import React, { useState } from 'react';

// Helper to get user initials
const getUserInitials = (user) => {
  if (!user) return '?';
  
  // Try different name formats
  const name = user.user_name || user.userName || `${user.first_name || ''} ${user.last_name || ''}`.trim();
  
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Helper to generate consistent color from user ID
const getUserColor = (userId) => {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-indigo-500',
    'bg-red-500',
    'bg-teal-500'
  ];
  
  const hash = String(userId).split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  
  return colors[Math.abs(hash) % colors.length];
};

// User avatar badge component
const UserAvatar = ({ user, className = '' }) => {
  const initials = getUserInitials(user);
  const colorClass = getUserColor(user.user_id || user.id);
  const userName = user.user_name || user.userName || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';

  return (
    <div
      className={`
        relative flex items-center justify-center
        w-7 h-7 rounded-full text-white text-xs font-medium
        border-2 border-white shadow-sm
        ${colorClass}
        ${className}
      `}
      title={userName}
    >
      {initials}
    </div>
  );
};

// Tooltip component for detailed status
const CollaborationTooltip = ({ isConnected, activeUsers, children, align = 'left' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const connectionMessage = isConnected 
    ? "Your changes are being automatically saved"
    : "We are trying to get you connected - hold tight! We will attempt to merge your changes when you reconnect";

  // Determine alignment classes
  const alignmentClasses = {
    left: 'left-0',
    center: 'left-1/2 -translate-x-1/2',
    right: 'right-0'
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className={`absolute top-full mt-2 z-[9999] w-80 p-4 bg-popover border rounded-lg shadow-lg ${alignmentClasses[align]}`}>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-orange-500'}`} />
              <span className="font-medium text-sm">
                {isConnected ? 'Connected' : 'Connecting...'}
              </span>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {connectionMessage}
            </p>
            
            {activeUsers && activeUsers.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium">Other people on this page:</p>
                <div className="space-y-1">
                  {activeUsers.slice(0, 10).map((user, index) => (
                    <div key={user.user_id || user.id || index} className="flex items-center gap-2">
                      <UserAvatar user={user} />
                      <div className="flex flex-col">
                        <span className="text-xs">
                          {user.user_name || user.userName || `${user.first_name} ${user.last_name}` || 'Unknown User'}
                        </span>
                        {user.editing_field && (
                          <span className="text-xs text-muted-foreground">
                            editing: {user.editing_field.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {activeUsers.length > 10 && (
                    <p className="text-xs text-muted-foreground">
                      and {activeUsers.length - 10} more...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main indicator component
const ConnectedIndicator = ({ isOnline = true, activeUsers = [], currentUserId = null, className = '', align = 'left' }) => {
  // Filter out the current user from active users
  const otherUsers = currentUserId 
    ? activeUsers.filter(user => (user.user_id || user.id) !== currentUserId)
    : activeUsers;
  
  // Show first 3 unique users (Google Docs style)
  const displayUsers = otherUsers.slice(0, 3);
  const additionalUsersCount = Math.max(0, otherUsers.length - 3);

  return (
    <CollaborationTooltip 
      isConnected={isOnline} 
      activeUsers={otherUsers}
      align={align}
    >
      <div className={`transition-all duration-200 hover:shadow-md rounded-lg cursor-default ${className}`}>
        <div className="p-2 h-10 flex items-center">
          <div className="flex items-center gap-3">
            {/* Status indicator */}
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full transition-colors ${
                isOnline 
                  ? 'bg-green-500 animate-pulse' 
                  : 'bg-orange-500 animate-pulse'
              }`} />
              <span className={`text-sm font-medium transition-colors ${
                isOnline ? 'text-green-700' : 'text-orange-700'
              }`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>

            {/* User avatars (Google Docs style) */}
            {displayUsers.length > 0 && (
              <div className="flex items-center">
                <div className="flex -space-x-2">
                  {displayUsers.map((user, index) => (
                    <div 
                      key={user.user_id || user.id || index} 
                      className="relative hover:z-10 transition-transform hover:scale-110"
                      style={{ zIndex: displayUsers.length - index }}
                    >
                      <UserAvatar user={user} />
                    </div>
                  ))}
                </div>
                
                {additionalUsersCount > 0 && (
                  <div className="ml-2 px-2 py-1 bg-muted rounded-full">
                    <span className="text-xs text-muted-foreground font-medium">
                      +{additionalUsersCount}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </CollaborationTooltip>
  );
};

const CollaborationIndicator = ({ isOnline = true, activeUsers = [], currentUserId = null, className = '', align = 'left' }) => {
  return <ConnectedIndicator isOnline={isOnline} activeUsers={activeUsers} currentUserId={currentUserId} className={className} align={align} />;
};

export default CollaborationIndicator;